// ============================================================
// ANSICHTSSACHE-AGENT – Karten einklappen
// ============================================================
// Eine Karte, die man einmal gelesen hat, soll aus dem Weg gehen
// koennen, ohne zu verschwinden: die Projektbeschreibung ueber den
// Aufgaben, eine erledigte Aufgabenliste zwischen offenen. Seit dem
// 27.09.2026 geht das im Haus und im Kundenbereich mit demselben
// Griff — diesem.
//
// DER WINKEL STEHT LINKS VOR DEM TITEL, das Menue „…“ ganz rechts
// (Ansage vom 13.10.2026, app-weit; bis dahin stand der Winkel als
// letzter Knopf rechts). Zu zeigt er nach rechts, offen nach unten —
// wie jeder Aufklapper vor einem Titel. Im Kopf haengt er per
// negativem Aussenabstand aus (`KLAPP_VORN`), damit das Zeichen auf
// der Textkante der Karte sitzt und nicht der Knopfrand.
//
// GEMERKT WIRD JE KARTE IM BROWSER, nicht in der Datenbank: wer
// eine Liste zuklappt, raeumt SEINE Sicht auf, nicht die der
// Kollegen. Ueber `useSyncExternalStore` wie beim Chatfenster
// (`chat/chat-dock.tsx`) — beim Serveraufbau ist alles offen, ein
// zweiter Reiter zieht mit.
// ============================================================

"use client";

import { useSyncExternalStore } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const ABLAGE = "ans-zu:";
const ABLAGE_EREIGNIS = "ans-zu-stand";

function standHorchen(melden: () => void) {
  window.addEventListener("storage", melden);
  window.addEventListener(ABLAGE_EREIGNIS, melden);
  return () => {
    window.removeEventListener("storage", melden);
    window.removeEventListener(ABLAGE_EREIGNIS, melden);
  };
}

/**
 * Ob die Karte mit diesem Schluessel zugeklappt ist, und der Wechsel.
 * Der Schluessel nennt die Sache, nicht die Seite
 * (`beschreibung:<projekt>`, `liste:<projekt>:<liste>`) — so ist
 * eine Liste im Haus und im Kundenbereich desselben Browsers dieselbe.
 */
export function useZugeklappt(schluessel: string) {
  const zu = useSyncExternalStore(
    standHorchen,
    () => window.localStorage.getItem(ABLAGE + schluessel) === "1",
    () => false,
  );

  function umschalten() {
    try {
      if (zu) window.localStorage.removeItem(ABLAGE + schluessel);
      else window.localStorage.setItem(ABLAGE + schluessel, "1");
    } catch {
      // Ohne Ablage bleibt die Karte offen — nichts geht verloren.
    }
    window.dispatchEvent(new Event(ABLAGE_EREIGNIS));
  }

  return [zu, umschalten] as const;
}

/**
 * Derselbe gemerkte Schalter, einmal gelesen statt beobachtet — fuer
 * einen Handgriff, der den Stand vieler Karten auf einmal braucht
 * (der Export der Kapazitaet nimmt nur die aufgeklappten Personen).
 * `true` heisst: der Schalter ist gesetzt.
 */
export function klappStandLesen(schluessel: string): boolean {
  try {
    return window.localStorage.getItem(ABLAGE + schluessel) === "1";
  } catch {
    return false;
  }
}

/**
 * Wie viele dieser Schalter gesetzt sind, beobachtet wie
 * `useZugeklappt` — fuer einen Sammelgriff („alle aufklappen“), der
 * wissen muss, ob schon alle stehen.
 */
export function useGesetzteKlappStaende(schluessel: string[]): number {
  return useSyncExternalStore(
    standHorchen,
    () => schluessel.filter((k) => window.localStorage.getItem(ABLAGE + k) === "1").length,
    () => 0,
  );
}

/** Setzt oder loescht viele Schalter auf einmal, mit EINER Meldung an alle Karten. */
export function klappStaendeSetzen(schluessel: string[], gesetzt: boolean) {
  try {
    for (const k of schluessel) {
      if (gesetzt) window.localStorage.setItem(ABLAGE + k, "1");
      else window.localStorage.removeItem(ABLAGE + k);
    }
  } catch {
    // Ohne Ablage bleibt alles, wie es war.
  }
  window.dispatchEvent(new Event(ABLAGE_EREIGNIS));
}

/** Wie der Winkel vorn im Kopf sitzt: ausgehaengt, das Zeichen auf der Kante. */
export const KLAPP_VORN = "-ml-1.5";

/**
 * Der Winkel im Kartenkopf, VOR dem Titel — 28 px wie das Menue „…“
 * am anderen Ende (eine Reihe, eine Hoehe).
 */
export function KlappKnopf({
  zu,
  onClick,
  className,
}: {
  zu: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={!zu}
      aria-label={zu ? "Aufklappen" : "Einklappen"}
      title={zu ? "Aufklappen" : "Einklappen"}
      className={cn(
        "text-text-tertiary duration-fast hover:bg-bg-tertiary hover:text-text-primary grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors",
        className,
      )}
    >
      <ChevronRight
        className={cn(
          "ease-standard duration-fast h-4 w-4 transition-transform",
          !zu && "rotate-90",
        )}
        aria-hidden
      />
    </button>
  );
}

/**
 * Dasselbe fuer Karten, die in einer Schleife entstehen — dort gibt
 * es keinen Ort fuer einen Hook je Karte.
 */
export function Klappe({
  schluessel,
  children,
}: {
  schluessel: string;
  children: (zu: boolean, umschalten: () => void) => React.ReactNode;
}) {
  const [zu, umschalten] = useZugeklappt(schluessel);
  return <>{children(zu, umschalten)}</>;
}

/**
 * Fuer Server-Seiten (Kundenbereich): Schalter und Inhalt als zwei
 * eigene Bauteile. Eine Server-Komponente kann keine Funktion als
 * Kind weiterreichen; beide lesen denselben Schluessel und damit
 * denselben Zustand.
 */
export function KlappSchalter({
  schluessel,
  className,
}: {
  schluessel: string;
  className?: string;
}) {
  const [zu, umschalten] = useZugeklappt(schluessel);
  return <KlappKnopf zu={zu} onClick={umschalten} className={className} />;
}

export function Zuklappbar({
  schluessel,
  children,
}: {
  schluessel: string;
  children: React.ReactNode;
}) {
  const [zu] = useZugeklappt(schluessel);
  return zu ? null : <>{children}</>;
}
